import React, {useCallback, useEffect, useRef} from 'react';
import {Box, HStack, IconButton, Tag} from '@chakra-ui/react';
import {useHorizontalScroll} from '@/hooks/useHorizontalScroll';
import {ChevronLeftIcon} from '@/components/svg/ChevronLeftIcon';
import {ChevronRightBlueIcon} from '@/components/svg/ChevronRightBlueIcon';
import {useSectionActiveTracker} from '@/hooks/cadencier/useSectionActiveTracker';

interface CadencierTagsProps {
  categories: {categorie_id: string; libelle_web: string}[];
  containerId?: string;
  headerContainerId?: string;
  headerOffset?: number;
  useDrawerScrolling?: boolean;
  tagContainerStyles?: React.CSSProperties;
}

/**
 * Tags de navigation par catégorie du cadencier.
 *
 * Responsabilités :
 *  1. Afficher une barre horizontale de tags (un par catégorie) avec dégradés/chevrons.
 *  2. Mettre en surbrillance le tag de la section en cours de lecture → délégué
 *     au hook `useSectionActiveTracker` (scroll listener déterministe).
 *  3. Au clic sur un tag : scroll animé vers la section correspondante, mise à
 *     jour optimiste du tag actif, et suspension du tracking pendant 3s pour
 *     éviter le clignotement pendant l'animation.
 *  4. Auto-centrage du tag actif dans la barre via `scrollIntoView` natif.
 */
export const CadencierTags: React.FC<CadencierTagsProps> = ({
  categories,
  containerId = 'cadencier_body_container',
  headerContainerId = 'cadencier_header_container',
  headerOffset = 130,
  useDrawerScrolling = false,
  tagContainerStyles = {},
}) => {
  const tagsScrollRef = useRef<HTMLDivElement>(null);

  // Hook de tracking : suit la section active au scroll et expose un setter
  // pour la mise à jour optimiste + un bloqueur temporaire au clic.
  const {
    activeSectionId: activeTag,
    setActiveSectionId: setActiveTag,
    blockTrackingForMs,
  } = useSectionActiveTracker({
    containerId,
    categories,
    useDrawerScrolling,
    headerOffset,
  });

  // Reset si la catégorie active disparait après filtrage
  useEffect(() => {
    if (categories.length === 0) return;
    if (!activeTag || !categories.some((c) => c.categorie_id === activeTag)) {
      setActiveTag(categories[0].categorie_id);
    }
  }, [categories, activeTag, setActiveTag]);

  // Centre automatiquement le tag actif dans la barre de tags.
  // Scroll horizontal manuel et non scrollIntoView
  useEffect(() => {
    if (!activeTag) return;
    const container = tagsScrollRef.current;
    const tag = document.getElementById(`tag_${activeTag}`);
    if (!container || !tag) return;
    const containerRect = container.getBoundingClientRect();
    const tagRect = tag.getBoundingClientRect();
    container.scrollTo({
      left:
        container.scrollLeft +
        (tagRect.left - containerRect.left) -
        (containerRect.width - tagRect.width) / 2,
      behavior: 'smooth',
    });
  }, [activeTag]);

  /**
   * Clic sur un tag → scroll vers la section correspondante
   */
  const scrollVersCategorie = useCallback(
    (
      categorie_id: string,
      // libelle_web: string,
      // index: number,
      // count: number,
    ) => {
      // Suspend le tracking auto pendant l'animation pour éviter que le scroll
      // traverse d'autres sections et les fasse clignoter.
      blockTrackingForMs(3000);

      // Mettre à jour le tag actif immédiatement pour l'UI
      setActiveTag(categorie_id);

      // Trouver l'élément cible
      const element = document.getElementById(categorie_id);
      if (!element) {
        console.warn('Élément non trouvé:', categorie_id);
        return;
      }

      const drawerRoot = useDrawerScrolling
        ? document.getElementById(containerId)
        : null;
      const headerHeight =
        document.getElementById(headerContainerId)?.clientHeight ?? 0;

      if (drawerRoot) {
        // Défilement dans un drawer : scroll relatif au container
        const tabsHeight =
          document.querySelector('.chakra-tabs__tablist')?.clientHeight ?? 0;
        const relativeOffset =
          element.getBoundingClientRect().top -
          drawerRoot.getBoundingClientRect().top;
        drawerRoot.scrollTo({
          top:
            drawerRoot.scrollTop +
            relativeOffset -
            headerHeight -
            tabsHeight -
            20,
          behavior: 'smooth',
        });
      } else {
        // Défilement standard dans le viewport
        window.scrollTo({
          top:
            element.getBoundingClientRect().top +
            window.scrollY -
            headerHeight -
            headerOffset,
          behavior: 'smooth',
        });
      }
    },
    [
      headerContainerId,
      headerOffset,
      useDrawerScrolling,
      containerId,
      blockTrackingForMs,
      setActiveTag,
    ],
  );

  // Chevrons de navigation horizontale
  const {canScrollLeft, canScrollRight, scrollToStart, scrollToEnd} =
    useHorizontalScroll(tagsScrollRef);

  return (
    <Box
      id={
        useDrawerScrolling
          ? 'cadencier-tags-container'
          : 'cadencier_header_tags'
      }
      position='sticky'
      top='0'
      zIndex='10'
      bg='white'
      width='100%'
      px={{base: '2', md: '4', lg: '6'}}
      style={tagContainerStyles}
    >
      {/* Dégradé gauche pour indiquer du contenu caché */}
      {canScrollLeft && (
        <Box
          position='absolute'
          left={0}
          top={0}
          bottom={0}
          w='30px'
          zIndex={1}
          pointerEvents='none'
          bgGradient='linear(to-r, white 70%, transparent)'
        />
      )}
      {/* Dégradé droit pour indiquer du contenu caché */}
      {canScrollRight && (
        <Box
          position='absolute'
          right={0}
          top={0}
          bottom={0}
          w='30px'
          zIndex={1}
          pointerEvents='none'
          bgGradient='linear(to-l, white 70%, transparent)'
        />
      )}
      {/* Chevron gauche */}
      {canScrollLeft && (
        <IconButton
          aria-label='Défiler vers la gauche'
          icon={<ChevronLeftIcon color='white' />}
          position='absolute'
          left={1}
          top='50%'
          transform='translateY(-50%)'
          zIndex={2}
          bg='gray.dark'
          borderRadius='full'
          h='19px'
          w='19px'
          minW='19px'
          p='0'
          ml={{base: '2', md: '4', lg: '6'}}
          _hover={{bg: 'black.text'}}
          onClick={scrollToStart}
        />
      )}

      {/* Chevron droit */}
      {canScrollRight && (
        <IconButton
          aria-label='Défiler vers la droite'
          icon={<ChevronRightBlueIcon color='white' />}
          position='absolute'
          right={1}
          top='50%'
          transform='translateY(-50%)'
          zIndex={2}
          bg='gray.dark'
          borderRadius='full'
          h='19px'
          w='19px'
          minW='19px'
          p='0'
          _hover={{bg: 'black.text'}}
          mr={{base: '2', md: '4', lg: '6'}}
          onClick={scrollToEnd}
        />
      )}

      {/* Défilement horizontal pour toutes les tailles d'écran */}
      <HStack
        ref={tagsScrollRef}
        spacing={2}
        py='3'
        overflowX='auto'
        maxW='100%'
        css={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {categories.map((cat) => {
          const isActive = activeTag === cat.categorie_id;
          return (
            <Tag
              key={`tag_${cat.categorie_id}`}
              id={`tag_${cat.categorie_id}`}
              size='md'
              cursor='pointer'
              h='39px'
              borderRadius='12px'
              fontSize='xs'
              bg={isActive ? 'blue.navy' : 'gray.bg'}
              color={isActive ? 'white' : 'gray.dark'}
              onClick={() => {
                scrollVersCategorie(
                  cat.categorie_id,
                  // cat.libelle_web,
                  // index,
                  // categories.length,
                );
              }}
              _hover={{
                bg: isActive ? 'gray.dark' : 'blue.navy',
                color: 'white',
              }}
              flexShrink={0}
            >
              {cat.libelle_web}
            </Tag>
          );
        })}
      </HStack>
    </Box>
  );
};
